import { useState } from 'react';
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
} from '@/components';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetUserCardTxs } from '@package/smart-wallet';
import { useForm } from 'react-hook-form';
import ReactJson from 'react-json-view';
import { z } from 'zod';

import { LoaderSkeleton } from './LoaderSkeleton';

/* Schemas */
export const UserCardTransactionsFormSchema = z.object({
  cardId: z.string().refine((value) => !value || !isNaN(Number(value)), {
    message: 'Card Id must be a valid number',
  }),
});

/* Types */
export type UserCardTransactionsFormType = z.infer<
  typeof UserCardTransactionsFormSchema
>;

/* Components */
export const UserCardTransactions = () => {
  const [cardId, setCardId] = useState<number | undefined>(undefined);

  /* Search Form */
  const searchForm = useForm<UserCardTransactionsFormType>({
    resolver: zodResolver(UserCardTransactionsFormSchema),
  });

  /* Handlers */
  const handleSearch = async (data: UserCardTransactionsFormType) => {
    setCardId(Number(data.cardId));
  };

  /* Render */
  return (
    <section className='w-2/3 flex flex-col items-center justify-center gap-4'>
      <Form {...searchForm}>
        <form
          onSubmit={searchForm.handleSubmit(handleSearch)}
          className='w-full flex flex-col items-center justify-center gap-4'
        >
          <section className='w-full flex flex-row items-center justify-center gap-4'>
            <FormField
              control={searchForm.control}
              name='cardId'
              render={({ field }) => (
                <FormItem className='w-full'>
                  <FormLabel>Card Id</FormLabel>
                  <FormControl>
                    <Input placeholder='1' type='number' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </section>
          <Button type='submit' className='cursor-pointer w-full'>
            Get card transactions
          </Button>
        </form>
      </Form>
      {!!cardId && <GetUserCardTransactions cardId={cardId} />}
    </section>
  );
};

/*Internal components */
type GetUserCardTransactionsProps = {
  cardId: number;
};

const GetUserCardTransactions = ({ cardId }: GetUserCardTransactionsProps) => {
  const { isLoading, records } = useGetUserCardTxs({
    cardId,
  });

  /* Render */
  return (
    <section className='w-full flex flex-col items-center justify-center'>
      <section className='w-full flex flex-col items-center justify-center gap-4'>
        {!!isLoading && <LoaderSkeleton />}
        {!isLoading && records && (
          <section className='w-full flex flex-col items-start justify-start gap-2'>
            <ReactJson collapsed src={records || {}} />
          </section>
        )}
      </section>
    </section>
  );
};
